import {
  Box,
  Form,
  FormErrorTypes,
  Modal,
  ModalContext,
  Text,
  useFormState,
  useFormValidation,
  useServerResponseTranslation,
} from 'dash-ui';
import {
  useGetAllFoldersInPropertyQuery,
  useMoveMediaToFolderMutation,
} from 'gql/generated.graphql';
import { type FormEvent, useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { DEFAULT_ROOT_MEDIA_FOLDER_NAME, RESERVED_MEDIA_FOLDER_NAME } from 'dash-common';

enum FormFields {
  MediaFolderId = 'mediaFolderId',
}

const MoveFileToFolderModal = ({
  onCompleted,
}: {
  onCompleted?: () => void;
}) => {
  const theme = useTheme();
  const { t } = useTranslation(['forms', 'common', 'pages']);
  const { isOpenCurrent, toggleModal, setPayload, payload } =
    useContext(ModalContext);
  const [
    formState,
    [setMediaFolderId],
    [formOnSubmit, formOnError, , , resetForm],
  ] = useFormState([FormFields.MediaFolderId]);

  const { data, loading: allMediaLoading } = useGetAllFoldersInPropertyQuery({
    variables: { 
      propertyId: payload?.propertyId,
      includeRootFolder: true // Include root folder to allow moving files back to it
    },
    fetchPolicy: 'network-only',
  });
  const [formErrors, , [setMediaFolderIdError], [resetFormErrors]] =
    useFormValidation([FormFields.MediaFolderId]);

  const translateErrorMessage = useServerResponseTranslation();
  const [moveMediaToFolderMutation, { loading: moveLoading }] =
    useMoveMediaToFolderMutation();

  const formRef = useRef<HTMLFormElement | null>(null);
  const folders = useMemo(() => data?.getAllFoldersInProperty || [], [data]);

  const onModalClose = () => {
    toggleModal(false, 'MoveFileToFolderModal');
    setPayload(null);
    resetFormErrors();
    resetForm();
  };

  const onSelectChange = (value: string) => {
    setMediaFolderId(value);
    setMediaFolderIdError(null);
  };

  const onSubmitForm = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    formOnSubmit();

    const { mediaFolderId } = formState;

    if (!mediaFolderId) {
      return setMediaFolderIdError({
        type: FormErrorTypes.Invalid,
        message: t('forms:messages.selectFolder'),
      });
    }

    moveMediaToFolderMutation({
      variables: {
        mediaFolderId,
        mediaIds: payload?.mediaIds,
        filter: payload?.filter,
        excludedIds: payload?.excludedIds,
      },
      ...(onCompleted && { onCompleted }),
    })
      .then(onModalClose)
      .catch((error) => {
        formOnError(translateErrorMessage(error));
      });
    return null;
  };

  return (
    <Modal
      isOpen={isOpenCurrent['MoveFileToFolderModal']}
      onRequestClose={onModalClose}
      title={t('pages:propertyPage.moveFileModalTitle') || ''}
      submitButton={{
        title: t('common:labels.move'),
        isLoading: moveLoading,
        disabled: moveLoading,
        onClick: () => {
          if (formRef.current) {
            formRef.current.requestSubmit();
          }
        },
      }}
      cancelButton={{ title: t('common:labels.cancel'), onClick: onModalClose }}
    >
      <Form ref={formRef} onSubmit={onSubmitForm}>
        <Box my={3}>
          <Text color={theme.colors.n80}>
            {t('pages:propertyPage.moveFileModalText')}
          </Text>
        </Box>
        <Form.Group>
          <Form.Label>{t('forms:placeholders.folderName')}</Form.Label>
          <Form.Select
            placeholder={t('forms:placeholders.folderName')}
            onChanged={onSelectChange}
            isLoading={allMediaLoading}
            options={folders.map((folder) => ({
              value: folder._id,
              label: folder.folderName === DEFAULT_ROOT_MEDIA_FOLDER_NAME 
                ? RESERVED_MEDIA_FOLDER_NAME 
                : folder.folderName,
            }))}
            $errorType={formErrors[FormFields.MediaFolderId]?.type}
          />
          <Form.Feedback
            $errorType={formErrors[FormFields.MediaFolderId]?.type}
          >
            {formErrors[FormFields.MediaFolderId]?.message}
          </Form.Feedback>
        </Form.Group>
      </Form>
    </Modal>
  );
};

export default MoveFileToFolderModal;
